"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Mail, Phone, MapPin, Clock, MessageCircle, Send } from "lucide-react"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import axios from "@/utils/axios"

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  })
  const [status, setStatus] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error message when user starts typing
    if (status === "error") {
      setStatus("")
      setErrorMessage("")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus("sending")
    setErrorMessage("")

    try {
      const response = await axios.post("/contact", {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      })

      if (response.data.success) {
        setStatus("success")
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" })
      } else {
        setStatus("error")
        setErrorMessage(response.data.message || "Failed to send message. Please try again.")
      }
    } catch (error) {
      console.error("Contact form error:", error)
      setStatus("error")
      
      // Extract error message from response
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message)
      } else if (error.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = error.response.data.errors
        if (Array.isArray(validationErrors)) {
          setErrorMessage(validationErrors.map(err => err.msg || err.message).join(", "))
        } else {
          setErrorMessage("Please check your input and try again.")
        }
      } else {
        setErrorMessage("Failed to send message. Please try again or contact us directly.")
      }
    }
  }

  return (
    <>
      <Seo {...routeSeoConfig.contact} type="website" />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/5">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary/10 to-secondary/10 py-16 lg:py-24">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <MessageCircle className="w-4 h-4" />
            We're Here to Help
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-primary leading-tight mb-6">
            Get in <span className="text-secondary">Touch</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions about our products or need personalized recommendations? Our beauty experts are
            ready to help you achieve your best skin and hair.
          </p>
        </div>
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/5 to-transparent"></div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-12">
            {/* Contact Form - Takes 2 columns */}
            <div className="lg:col-span-2">
              <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-8">
                  <CardTitle className="text-3xl font-bold text-primary flex items-center gap-3">
                    <Send className="w-8 h-8 text-secondary" />
                    Send us a Message
                  </CardTitle>
                  <p className="text-gray-600 text-lg">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-primary mb-2">
                          Your Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-primary mb-2">
                          Your Email *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john.doe@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-primary mb-2">
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+234 xxx xxx xxxx"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-primary mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        placeholder="How can we help you today?"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-primary mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell us more about your inquiry..."
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50 resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 text-lg h-14 rounded-xl"
                      disabled={status === "sending"}
                    >
                      {status === "sending" ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Sending Message...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          Send Message
                        </div>
                      )}
                    </Button>
                    {status === "success" && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <p className="text-green-700 text-center font-medium">
                          ✨ Your message has been sent successfully! We'll get back to you soon.
                        </p>
                      </div>
                    )}
                    {status === "error" && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <p className="text-red-700 text-center font-medium">
                          ❌ {errorMessage || "Failed to send message. Please try again or contact us directly."}
                        </p>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information Sidebar */}
            <div className="space-y-6">
              {/* Contact Details */}
              <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-primary">Contact Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Visit Our Store</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Aazi -Otaru Close
                        <br />
                        Okene, Kogi State
                        <br />
                        Nigeria
                      </p>
                    </div>
                  </div> */}

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">WhatsApp Us</h4>
                      <p className="text-gray-600 text-sm">+2348073382477</p>
                      <p className="text-gray-500 text-xs mt-1">Mon-Fri, 9AM-6PM WAT</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Email Us</h4>
                      <p className="text-gray-600 text-sm">hello@fayozltd.com</p>
                      <p className="text-gray-500 text-xs mt-1">We reply within 24 hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business Hours */}
              <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-primary flex items-center gap-2">
                    <Clock className="w-6 h-6 text-secondary" />
                    Business Hours
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="font-medium text-primary">Monday - Friday</span>
                    <span className="text-gray-600">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="font-medium text-primary">Saturday</span>
                    <span className="text-gray-600">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-medium text-primary">Sunday</span>
                    <span className="text-red-500 font-medium">Closed</span>
                  </div>
                  <div className="mt-4 p-3 bg-secondary/10 rounded-lg">
                    <p className="text-sm text-gray-600 text-center">
                      <strong>Holiday Hours:</strong> Please check our social media for special holiday schedules.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Partner With Us
              </div>
              <h2 className="text-3xl lg:text-5xl font-bold text-primary leading-tight mb-6">
                Let's Grow <span className="text-secondary">Together</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We're always looking for passionate individuals and businesses to join our mission of bringing premium beauty products to everyone. Explore partnership opportunities with Fayoz.
              </p>
            </div>

            {/* Partnership Types */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {/* Affiliate Partners */}
              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary/20 to-secondary/40 rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">Affiliate Partners</h3>
                  <p className="text-gray-600 mb-4">
                    Earn commissions by promoting our products. Get exclusive links, marketing materials, and dedicated support.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-500">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-secondary rounded-full"></span>
                      Competitive commission rates
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-secondary rounded-full"></span>
                      Real-time tracking dashboard
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-secondary rounded-full"></span>
                      Monthly payouts
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Influencers & Creators */}
              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/40 rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">Influencers & Creators</h3>
                  <p className="text-gray-600 mb-4">
                    Collaborate with us on content creation. Get free products, exclusive deals, and grow your audience.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-500">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      Free product samples
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      Exclusive discount codes
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      Featured on our platforms
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Wholesale & Retail */}
              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 md:col-span-2 lg:col-span-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-200 to-green-400 rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">Wholesale & Retail</h3>
                  <p className="text-gray-600 mb-4">
                    Stock Fayoz products in your store. Enjoy wholesale pricing, marketing support, and priority shipping.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-500">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      Bulk order discounts
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      Marketing materials included
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      Dedicated account manager
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Why Partner With Us */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-12 shadow-xl mb-16">
              <div className="text-center mb-10">
                <h3 className="text-2xl lg:text-3xl font-bold text-primary mb-4">Why Partner With Fayoz?</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Join a growing community of partners who are passionate about beauty and skincare.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-6">
                  <div className="w-14 h-14 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-primary mb-2">Great Earnings</h4>
                  <p className="text-sm text-gray-500">Competitive commissions and incentives</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-primary mb-2">Trusted Brand</h4>
                  <p className="text-sm text-gray-500">Premium quality products customers love</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-primary mb-2">Dedicated Support</h4>
                  <p className="text-sm text-gray-500">Personal account manager for partners</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-primary mb-2">Growth Potential</h4>
                  <p className="text-sm text-gray-500">Expand with a rapidly growing company</p>
                </div>
              </div>
            </div>

            {/* Partnership Contact Form */}
            <Card className="shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
              <CardHeader className="pb-6 text-center">
                <CardTitle className="text-2xl lg:text-3xl font-bold text-primary">
                  Become a Partner
                </CardTitle>
                <p className="text-gray-600 mt-2">
                  Fill out the form below and our partnership team will get back to you within 48 hours.
                </p>
              </CardHeader>
              <CardContent>
                <form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-primary mb-2">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="Your full name"
                        required
                        className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-primary mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        required
                        className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-primary mb-2">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        placeholder="+234 xxx xxx xxxx"
                        className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-primary mb-2">
                        Partnership Type *
                      </label>
                      <select
                        required
                        className="w-full h-12 px-3 border border-gray-200 rounded-md focus:border-secondary focus:ring-secondary/20 bg-white/50 text-gray-700"
                      >
                        <option value="">Select partnership type</option>
                        <option value="affiliate">Affiliate Partner</option>
                        <option value="influencer">Influencer / Content Creator</option>
                        <option value="wholesale">Wholesale / Retail</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Company / Brand Name (if applicable)
                    </label>
                    <Input
                      type="text"
                      placeholder="Your company or brand name"
                      className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Tell Us About Yourself *
                    </label>
                    <Textarea
                      placeholder="Tell us about your business, audience, or why you'd like to partner with us..."
                      rows={5}
                      required
                      className="border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Website / Social Media Links
                    </label>
                    <Input
                      type="url"
                      placeholder="https://your-website.com or @instagram_handle"
                      className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 text-lg h-14 rounded-xl"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Submit Partnership Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
